/**
 * Archivos PRIVADOS dentro de /uploads: CURP, foto y comprobante de la
 * inscripción (public-registrations), comprobantes de membresía
 * (club-memberships) y la credencial de cada jugador o socio (credentials,
 * club-credentials: todo menos plantillas y logos). Se abren con liga firmada
 * de 1 h y el servidor responde `Cache-Control: private, no-store`, pero
 * Workbox ignora esa cabecera: sin esta exclusión el service worker guardaría
 * 30 días en el teléfono una copia de la CURP de un menor, de un comprobante
 * bancario o de una credencial con foto y fecha de nacimiento.
 *
 * Misma regla que el servidor (`is_private_upload_relative` en
 * backend/app/core/private_uploads.py): en las carpetas de credenciales lo
 * privado es TODO salvo `templates/` y `logos/`.
 */

import { T123_UPLOADS_CACHE } from "./cacheNames";

const PRIVATE_UPLOAD_PATH = /\/uploads\/+(?:public-registrations|club-memberships)(?:\/|$)/i;
const CREDENTIAL_FOLDER_PATH = /\/uploads\/+(credentials|club-credentials)(?:\/+(.*))?$/i;
const PUBLIC_CREDENTIAL_ASSET = {
  credentials: /^tournament-\d{1,12}\/+(?:templates|logos)\/+[A-Za-z0-9][A-Za-z0-9._-]{0,200}$/i,
  "club-credentials": /^club-\d{1,12}\/+logos\/+[A-Za-z0-9][A-Za-z0-9._-]{0,200}$/i,
} as const;

export function isPrivateUploadPath(pathname: string): boolean {
  if (PRIVATE_UPLOAD_PATH.test(pathname)) return true;
  const credential = CREDENTIAL_FOLDER_PATH.exec(pathname);
  if (!credential) return false;
  const folder = credential[1].toLowerCase() as keyof typeof PUBLIC_CREDENTIAL_ASSET;
  return !PUBLIC_CREDENTIAL_ASSET[folder].test(credential[2] ?? "");
}

/** Uploads que SÍ se pueden guardar en el caché del SW (logos, avatares, plantillas…). */
export function isCacheableUploadPath(pathname: string): boolean {
  return pathname.includes("/uploads/") && !isPrivateUploadPath(pathname);
}

type CacheLike = Pick<Cache, "keys" | "delete">;
type CacheStorageLike = Pick<CacheStorage, "has" | "open">;

/**
 * Borra del caché de uploads las copias privadas que guardaron versiones
 * anteriores del service worker. Best effort: nunca bloquea la activación.
 */
export async function purgePrivateUploadEntries(
  cacheStorage: CacheStorageLike | undefined = typeof caches !== "undefined" ? caches : undefined,
): Promise<number> {
  if (!cacheStorage) return 0;
  try {
    if (!(await cacheStorage.has(T123_UPLOADS_CACHE))) return 0;
    const cache: CacheLike = await cacheStorage.open(T123_UPLOADS_CACHE);
    const requests = await cache.keys();
    let removed = 0;
    for (const request of requests) {
      let pathname: string;
      try {
        pathname = new URL(request.url).pathname;
      } catch {
        continue;
      }
      if (isPrivateUploadPath(pathname) && (await cache.delete(request))) removed += 1;
    }
    return removed;
  } catch {
    return 0;
  }
}
